// Por dónde se da de alta un pedido.
//
// Hay DOS caminos y acaban en sitios distintos: el de siempre escribe en
// Dataverse, y el nuevo se lo encarga a un flujo de Power Automate que tarda
// minutos. Esconder esa diferencia detrás de un único botón «Nuevo pedido»
// haría que dos acciones con tiempos y resultados distintos pareciesen la misma.
//
// Por eso se pregunta antes, y la tarjeta dice cuánto tarda cada una.
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

interface Props {
  dark: boolean
  onManual: () => void
  onArchivo: () => void
  onClose: () => void
}

export function ElegirOrigenPedido({ dark, onManual, onArchivo, onClose }: Props) {
  const { t } = useTranslation()

  useEffect(() => {
    const fn = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', fn)
    return () => document.removeEventListener('keydown', fn)
  }, [onClose])

  const bg = dark ? 'bg-[#0b0f16]' : 'bg-white'
  const border = dark ? 'border-white/[0.07]' : 'border-slate-200'
  const mainTxt = dark ? 'text-slate-100' : 'text-slate-900'
  const mutedTxt = dark ? 'text-slate-400' : 'text-slate-500'
  const dimTxt = dark ? 'text-slate-600' : 'text-slate-400'
  const tarjeta = dark
    ? 'border-white/[0.08] bg-white/[0.03] hover:border-sky-500/50 hover:bg-sky-500/[0.06]'
    : 'border-slate-200 bg-slate-50 hover:border-sky-400 hover:bg-sky-50'

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4">
      <div onClick={onClose} className="absolute inset-0 bg-black/50 backdrop-blur-[2px] cursor-pointer" />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('salesOrders.origen.titulo')}
        className={`relative w-full max-w-lg rounded-2xl border shadow-2xl ${bg} ${border}`}
      >
        <div className={`flex items-start gap-4 px-6 pt-5 pb-4 border-b ${border}`}>
          <div className="flex-1 min-w-0">
            <h2 className={`text-[14px] font-semibold ${mainTxt}`}>
              {t('salesOrders.origen.titulo')}
            </h2>
            <p className={`text-[11px] mt-1 ${mutedTxt}`}>{t('salesOrders.origen.subtitulo')}</p>
          </div>
          <button
            onClick={onClose}
            aria-label={t('salesOrders.nuevaCarga.cerrar')}
            className={`p-1.5 rounded-lg cursor-pointer transition-colors ${
              dark ? 'text-slate-500 hover:text-slate-300 hover:bg-white/6'
                   : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
            }`}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-5">
          <button
            type="button"
            onClick={onArchivo}
            className={`flex flex-col items-start gap-2.5 p-4 rounded-xl border text-left cursor-pointer transition-all ${tarjeta}`}
          >
            <span className={`w-9 h-9 rounded-lg grid place-items-center ${
              dark ? 'bg-sky-500/15 text-sky-400' : 'bg-sky-100 text-sky-600'
            }`}>
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 16V4M12 4l-4 4M12 4l4 4" />
                <path d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2" />
              </svg>
            </span>
            <span className={`text-[13px] font-semibold ${mainTxt}`}>
              {t('salesOrders.origen.archivo')}
            </span>
            <span className={`text-[11px] leading-relaxed ${mutedTxt}`}>
              {t('salesOrders.origen.archivoTexto')}
            </span>
            <span className={`text-[10px] mt-auto pt-1 ${dimTxt}`}>
              {t('salesOrders.origen.archivoTiempo')}
            </span>
          </button>

          <button
            type="button"
            onClick={onManual}
            className={`flex flex-col items-start gap-2.5 p-4 rounded-xl border text-left cursor-pointer transition-all ${tarjeta}`}
          >
            <span className={`w-9 h-9 rounded-lg grid place-items-center ${
              dark ? 'bg-white/[0.06] text-slate-400' : 'bg-white text-slate-500'
            }`}>
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 20h9M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4z" />
              </svg>
            </span>
            <span className={`text-[13px] font-semibold ${mainTxt}`}>
              {t('salesOrders.origen.manual')}
            </span>
            <span className={`text-[11px] leading-relaxed ${mutedTxt}`}>
              {t('salesOrders.origen.manualTexto')}
            </span>
            <span className={`text-[10px] mt-auto pt-1 ${dimTxt}`}>
              {t('salesOrders.origen.manualTiempo')}
            </span>
          </button>
        </div>
      </div>
    </div>
  )
}
